// Client-side tex diffing built on `diff-match-patch`.
//
// We compute a semantic character-level diff, then normalise it into a
// line-oriented shape so the UI can render an unified/split view. Working at
// the character level first (instead of jumping straight to line-diff) means
// small in-line changes ("Section 2" → "Section 3") don't drown the reader in
// whole-line adds/removes.
//
// Everything here is pure — no React, no Supabase — so it stays unit-testable
// and cheap to memoize per version pair.

import DiffMatchPatch from 'diff-match-patch';

export type DiffOp = 'equal' | 'insert' | 'delete';

export interface DiffLine {
  op: DiffOp;
  /** Raw line text (no trailing newline). */
  text: string;
}

export interface SectionDiff {
  /** Section anchor / id, or a synthetic key when we fall back to whole-file. */
  key: string;
  /** Human-readable heading — falls back to the anchor. */
  title: string;
  lines: DiffLine[];
  added: number;
  removed: number;
  /** True when both sides are byte-identical. Callers can hide these by default. */
  unchanged: boolean;
}

const dmp = new DiffMatchPatch();
// Cap semantic cleanup work so pathological inputs don't hang the UI.
dmp.Diff_Timeout = 1.0;

/**
 * Diff two blobs of text. Returns line-oriented ops suitable for a diff view.
 * Character-level diffs are folded into per-line ops: a line is `insert` /
 * `delete` if it exists on only one side, `equal` when both sides match.
 */
export function diffTex(base: string, head: string): DiffLine[] {
  if (base === head) {
    return splitLines(base).map((text) => ({ op: 'equal' as const, text }));
  }

  // Line-mode diff (recommended pattern from diff-match-patch for text files):
  // encode each unique line as a single Unicode char, diff those, then expand.
  const { chars1, chars2, lineArray } = dmp.diff_linesToChars_(base, head);
  const raw = dmp.diff_main(chars1, chars2, false);
  dmp.diff_charsToLines_(raw, lineArray);
  dmp.diff_cleanupSemantic(raw);

  const out: DiffLine[] = [];
  for (const [op, text] of raw) {
    const lines = splitLines(text);
    // diff-match-patch keeps trailing '' entries after a final newline; drop
    // the empty tail so we don't render phantom rows.
    if (lines.length && lines[lines.length - 1] === '' && text.endsWith('\n')) {
      lines.pop();
    }
    for (const line of lines) {
      out.push({
        op: op === 0 ? 'equal' : op === 1 ? 'insert' : 'delete',
        text: line,
      });
    }
  }
  return out;
}

/**
 * Split into per-section diffs when we have structured snapshots for both
 * versions. Falls back to a single whole-file diff otherwise. Sections that
 * exist only on one side become full insert/delete blocks.
 */
export function diffSections(
  base: Array<{ anchor: string; title?: string | null; body: string }>,
  head: Array<{ anchor: string; title?: string | null; body: string }>,
): SectionDiff[] {
  const baseMap = new Map(base.map((s) => [s.anchor, s]));
  const headMap = new Map(head.map((s) => [s.anchor, s]));
  // Preserve head order; append sections that only exist in base at the end.
  const anchors: string[] = [];
  for (const s of head) anchors.push(s.anchor);
  for (const s of base) if (!headMap.has(s.anchor)) anchors.push(s.anchor);

  return anchors.map((anchor) => {
    const b = baseMap.get(anchor);
    const h = headMap.get(anchor);
    const title = h?.title || b?.title || anchor;
    const baseBody = b?.body ?? '';
    const headBody = h?.body ?? '';
    const lines = diffTex(baseBody, headBody);
    const added = lines.filter((l) => l.op === 'insert').length;
    const removed = lines.filter((l) => l.op === 'delete').length;
    return {
      key: anchor,
      title,
      lines,
      added,
      removed,
      unchanged: added === 0 && removed === 0,
    };
  });
}

/** Wrap a whole-file diff in the same shape as `diffSections` so the UI can
 * render both flavours through one path. */
export function diffAsSingleFile(base: string, head: string, title = 'Document'): SectionDiff {
  const lines = diffTex(base, head);
  const added = lines.filter((l) => l.op === 'insert').length;
  const removed = lines.filter((l) => l.op === 'delete').length;
  return {
    key: '__file__',
    title,
    lines,
    added,
    removed,
    unchanged: added === 0 && removed === 0,
  };
}

function splitLines(text: string): string[] {
  if (text === '') return [];
  return text.split('\n');
}
